
import * as XLSX from 'xlsx';

const STATUS_LABEL = {
  'full-day': 'Full Day',
  mismatch: 'Mismatch',
  present: 'Present',
  'half-day': 'Half Day',
  'missing-punch': 'Missing Punch',
  'no-record': 'No Attendance Recorded',
  leave: 'Leave',
  'half-leave': 'Half Day Leave',
  holiday: 'Holiday',
  sunday: 'Sunday',
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function stamp(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};


/* Decimal hours */
const formatHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number(n.toFixed(2))} h`;
};

/* Minutes → decimal hours */
const formatLateHours = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number((n / 60).toFixed(2))} h`;
};


export function exportAttendanceOverviewToExcel({ rows = [], filters }) {
  const activeFilters = Object.entries(filters || {})
    .filter(([, v]) => v && v !== 'All')
    .map(([k, v]) => `${k}: ${v}`)
    .join('   |   ') || 'None';

  const headerRows = [
    ['ATTENDANCE OVERVIEW'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Records: ${rows.length}`],
    [],
  ];

  const columns = [
    { key: 'ServiceNo', label: 'Service No', width: 12 },
    { key: 'Name', label: 'Name', width: 25 },
    { key: 'Date', label: 'Date', width: 12 },
    { key: 'Day', label: 'Day', width: 10 },
    {
      key: 'Status',
      label: 'Status',
      width: 16,
      format: (r) => STATUS_LABEL[r.Status] || r.Status || '',
    },
    { key: 'InTime', label: 'In Time', width: 10 },
    { key: 'OutTime', label: 'Out Time', width: 10 },
    { key: 'LateMinutes', label: 'Late Hours', width: 14, lateHours: true },
    { key: 'TotalHours', label: 'Total Hours', width: 14, time: true },
    { key: 'NormalHours', label: 'Normal Hours', width: 14, time: true },
    { key: 'OTHours', label: 'OT Hours', width: 12, time: true },
    { key: 'PreOT', label: 'Pre OT Hours', width: 14, time: true },
    { key: 'PostOT', label: 'Post OT Hours', width: 14, time: true },
    // { key: 'PrePostOT', label: 'Pre & Post OT Hours', width: 20, time: true },
    { key: 'HolidayOT', label: 'Holiday OT Hours', width: 16, time: true },
    { key: 'SundayOT', label: 'Sunday OT Hours', width: 16, time: true },
    { key: 'Department', label: 'Department', width: 22 },
    { key: 'Location', label: 'Location', width: 22 },
    { key: 'Category', label: 'Category', width: 18 },
  ];

  const columnHeader = columns.map((c) => c.label);

  const bodyRows = rows.map((r) =>
    columns.map((c) => {
      if (c.format) return c.format(r);

      if (c.lateHours) {
        return formatLateHours(r[c.sourceKey || c.key]);
      }

      if (c.time) {
        return formatHours(r[c.sourceKey || c.key]);
      }

      const v = r[c.key];
      return v === null || v === undefined ? '' : v;
    })
  );

  /* Totals row */
  const totalsRow = columns.map((c) => {
    if (c.key === 'ServiceNo') {
      return `TOTAL — ${rows.length} record${rows.length === 1 ? '' : 's'}`;
    }

    if (c.lateHours) {
      const totalMinutes = rows.reduce(
        (sum, r) => sum + num(r[c.sourceKey || c.key]),
        0
      );

      return formatLateHours(totalMinutes);
    }

    if (c.time) {
      const totalHours = rows.reduce(
        (sum, r) => sum + num(r[c.sourceKey || c.key]),
        0
      );

      return formatHours(totalHours);
    }

    return '';
  });

  const aoa = [...headerRows, columnHeader, ...bodyRows, [], totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = columns.map((c) => ({ wch: c.width || 12 }));

  const lastCol = columns.length - 1;

  ws['!merges'] = headerRows.slice(0, 4).map((_, i) => ({
    s: { r: i, c: 0 },
    e: { r: i, c: lastCol },
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Overview');

  const d = new Date();

  const filename =
    `Attendance_Overview_${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}.xlsx`;

  XLSX.writeFile(wb, filename);

  return filename;
}